package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.PreMadeCharacter
import fr.manyverse.app.domain.model.UniverseConfig
import fr.manyverse.app.presentation.components.gm_screen.PreMadeCharacterFormDialog
import fr.manyverse.app.presentation.components.premade.PreMadeCarousel
import fr.manyverse.app.presentation.components.premade.PreMadeCharacterCard
import fr.manyverse.app.presentation.components.premade.rememberPreMadeCarouselState
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.theme.UniverseTheme
import fr.manyverse.app.presentation.viewmodel.PreMadeCharactersViewModel
import fr.manyverse.app.presentation.viewmodel.ThemeViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.add_premade
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.delete_premade
import manyverse.shared.generated.resources.delete_premade_confirm
import manyverse.shared.generated.resources.delete_premade_message
import manyverse.shared.generated.resources.edit_premade
import manyverse.shared.generated.resources.error_loading_premades
import manyverse.shared.generated.resources.no_premades_yet
import manyverse.shared.generated.resources.premade_characters
import manyverse.shared.generated.resources.retry
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PreMadeCharactersScreen(
    tableId: String,
    themeId: String,
    themeSeedColor: Long?,
    isFreeConstruction: Boolean,
    isCharacterSheetAvailable: Boolean,
    onNavigateBack: () -> Unit
) {
    val themeViewModel: ThemeViewModel = koinViewModel()
    val themePreference by themeViewModel.isDarkTheme.collectAsStateWithLifecycle()
    val isDarkTheme = themePreference ?: isSystemInDarkTheme()
    val universeConfig = remember(themeId, themeSeedColor) {
        UniverseConfig(themeId = themeId, themeSeedColor = themeSeedColor)
    }

    UniverseTheme(config = universeConfig, isDark = isDarkTheme) {

    val viewModel: PreMadeCharactersViewModel = koinViewModel(
        key = "premades_${tableId}"
    ) { parametersOf(tableId) }

    val listState by viewModel.listState.collectAsStateWithLifecycle()
    val deleteState by viewModel.deleteState.collectAsStateWithLifecycle()

    var showCreateDialog by remember { mutableStateOf(false) }
    var editTarget by remember { mutableStateOf<PreMadeCharacter?>(null) }
    var deleteTarget by remember { mutableStateOf<PreMadeCharacter?>(null) }

    LaunchedEffect(deleteState) {
        if (deleteState is AsyncUiState.Success) {
            viewModel.resetDeleteState()
            deleteTarget = null
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.premade_characters)) },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = stringResource(Res.string.back)
                        )
                    }
                }
            )
        },
        floatingActionButton = {
            if (listState is AsyncUiState.Success) {
                FloatingActionButton(
                    onClick = { showCreateDialog = true },
                    containerColor = MaterialTheme.colorScheme.primaryContainer,
                ) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = stringResource(Res.string.add_premade),
                        tint = MaterialTheme.colorScheme.onPrimaryContainer,
                    )
                }
            }
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            when (val state = listState) {
                is AsyncUiState.Loading, is AsyncUiState.Idle -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }

                is AsyncUiState.Error -> {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(24.dp),
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = stringResource(Res.string.error_loading_premades),
                            color = MaterialTheme.colorScheme.error,
                            textAlign = TextAlign.Center
                        )
                        Text(
                            text = state.message,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(top = 8.dp)
                        )
                        Button(
                            onClick = { viewModel.load() },
                            modifier = Modifier.padding(top = 16.dp)
                        ) {
                            Text(stringResource(Res.string.retry))
                        }
                    }
                }

                is AsyncUiState.Success -> {
                    val items = state.data
                    if (items.isEmpty()) {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(24.dp),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = stringResource(Res.string.no_premades_yet),
                                style = MaterialTheme.typography.bodyLarge,
                                textAlign = TextAlign.Center,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    } else {
                        PreMadeCarouselSection(
                            items = items,
                            onEdit = { editTarget = it },
                            onDelete = { deleteTarget = it }
                        )
                    }
                }
            }
        }
    }

    if (showCreateDialog) {
        PreMadeCharacterFormDialog(
            viewModel = viewModel,
            existing = null,
            isFreeConstruction = isFreeConstruction,
            isCharacterSheetAvailable = isCharacterSheetAvailable,
            onDismiss = { showCreateDialog = false }
        )
    }

    editTarget?.let { target ->
        PreMadeCharacterFormDialog(
            viewModel = viewModel,
            existing = target,
            isFreeConstruction = isFreeConstruction,
            isCharacterSheetAvailable = isCharacterSheetAvailable,
            onDismiss = { editTarget = null }
        )
    }

    deleteTarget?.let { target ->
        DeletePreMadeDialog(
            isLoading = deleteState is AsyncUiState.Loading,
            errorMessage = (deleteState as? AsyncUiState.Error)?.message,
            onConfirm = { viewModel.delete(target.id) },
            onDismiss = {
                viewModel.resetDeleteState()
                deleteTarget = null
            }
        )
    }

    } // UniverseTheme
}

@Composable
private fun PreMadeCarouselSection(
    items: List<PreMadeCharacter>,
    onEdit: (PreMadeCharacter) -> Unit,
    onDelete: (PreMadeCharacter) -> Unit
) {
    val pagerState = rememberPreMadeCarouselState(items.size)
    PreMadeCarousel(
        items = items,
        pagerState = pagerState,
        modifier = Modifier.fillMaxSize()
    ) { item ->
        PreMadeCharacterCard(
            preMade = item,
            actions = {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp, Alignment.CenterHorizontally)
                ) {
                    IconButton(onClick = { onEdit(item) }) {
                        Icon(
                            imageVector = Icons.Default.Edit,
                            contentDescription = stringResource(Res.string.edit_premade)
                        )
                    }
                    IconButton(onClick = { onDelete(item) }) {
                        Icon(
                            imageVector = Icons.Default.Delete,
                            contentDescription = stringResource(Res.string.delete_premade),
                            tint = MaterialTheme.colorScheme.error
                        )
                    }
                }
            }
        )
    }
}

@Composable
private fun DeletePreMadeDialog(
    isLoading: Boolean,
    errorMessage: String?,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(stringResource(Res.string.delete_premade_confirm)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(stringResource(Res.string.delete_premade_message))
                if (errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = onConfirm,
                enabled = !isLoading
            ) {
                if (isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(16.dp),
                        strokeWidth = 2.dp,
                        color = MaterialTheme.colorScheme.onPrimary
                    )
                } else {
                    Text(stringResource(Res.string.delete_premade))
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !isLoading) {
                Text(stringResource(Res.string.cancel))
            }
        }
    )
}
